import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import {
  Code, Database, Globe, Bot, ArrowRight, Shield,
  BookOpen, MessageSquare, Eye
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const WhatIsLLMOPage = () => (
  <div className="min-h-screen pt-28 pb-20" data-testid="what-is-llmo-page">
    <Helmet>
      <title>What Is LLMO? LLM Optimization Explained (2026) | Digital Media Ninja</title>
      <meta name="description" content="LLM Optimization (LLMO) is the practice of influencing how Large Language Models perceive, reference, and recommend your brand. Complete guide." />
      <link rel="canonical" href="https://digitalmedianinja.com/what-is-llmo/" />
    </Helmet>

    <article className="container-custom max-w-4xl mx-auto px-4" data-speakable="true">
      <div className="mb-12">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm font-medium mb-6">
          <Code className="w-4 h-4" /> Definition Guide
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6 leading-tight">What Is LLM Optimization (LLMO)?</h1>
        <p className="text-lg text-gray-300 leading-relaxed mb-6" data-speakable="true">
          <strong className="text-white">LLM Optimization (LLMO)</strong> is the practice of influencing how Large Language Models — such as ChatGPT (GPT-5), Claude, Gemini, and the models powering Perplexity and Microsoft Copilot — perceive, reference, and recommend your brand. LLMO is the umbrella discipline that encompasses both GEO (Generative Engine Optimization) and AEO (Answer Engine Optimization), addressing the underlying model layer of AI search visibility.
        </p>
      </div>

      <section className="mb-12">
        <h2 className="text-2xl font-bold text-white mb-6">The AI Recommendation Pipeline</h2>
        <p className="text-gray-300 leading-relaxed mb-6">
          LLMs don't randomly pick which businesses to recommend. They follow a deterministic pipeline that LLMO targets at every stage:
        </p>
        <div className="space-y-4">
          {[
            { step: "1", title: "Training Data", desc: "LLMs learn from billions of web pages during pre-training. If your brand appears frequently on high-authority sites (Wikipedia, industry publications, trusted directories), the model internalizes your brand as relevant to your industry.", icon: Database },
            { step: "2", title: "Retrieval (RAG)", desc: "When a user asks a question, the LLM's Retrieval-Augmented Generation system fetches relevant current content from the web. Your pages must rank well in these retrieval indexes — which share overlap with, but are not identical to, Google's index.", icon: Globe },
            { step: "3", title: "Entity Matching", desc: "The LLM maps the user's query to known entities — brands, services, locations. Strong entity signals (consistent structured data, Knowledge Graph presence, unambiguous NAP) increase the probability of your brand being matched to relevant queries.", icon: Bot },
            { step: "4", title: "Confidence Scoring", desc: "Before including your brand in a response, the LLM calculates a confidence score. Multiple authoritative, consistent mentions across diverse sources increase confidence. Contradictory information decreases it.", icon: Shield },
          ].map((item, i) => (
            <div key={i} className="flex gap-5 items-start bg-white/5 border border-white/10 rounded-xl p-5">
              <div className="w-10 h-10 rounded-full bg-blue-500/10 border border-blue-500/20 flex items-center justify-center shrink-0">
                <span className="text-blue-400 font-bold">{item.step}</span>
              </div>
              <div>
                <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                <p className="text-gray-400 text-sm">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-bold text-white mb-6">LLMO vs. GEO vs. AEO</h2>
        <div className="bg-white/5 border border-white/10 rounded-xl p-5 space-y-4">
          <div className="flex gap-3 items-start">
            <div className="w-8 h-8 rounded bg-blue-500/10 flex items-center justify-center shrink-0"><Code className="w-4 h-4 text-blue-400" /></div>
            <div><h3 className="text-white font-semibold text-sm">LLMO (LLM Optimization)</h3><p className="text-gray-400 text-xs">The umbrella discipline. Influences how AI models perceive your brand at the model layer — training data, entity recognition, confidence scoring.</p></div>
          </div>
          <div className="flex gap-3 items-start">
            <div className="w-8 h-8 rounded bg-purple-500/10 flex items-center justify-center shrink-0"><Bot className="w-4 h-4 text-purple-400" /></div>
            <div><h3 className="text-white font-semibold text-sm">GEO (Generative Engine Optimization)</h3><p className="text-gray-400 text-xs">A subset of LLMO. Focuses on the front-end AI search interfaces — ChatGPT, AI Overviews, Perplexity. The "output layer."</p></div>
          </div>
          <div className="flex gap-3 items-start">
            <div className="w-8 h-8 rounded bg-emerald-500/10 flex items-center justify-center shrink-0"><MessageSquare className="w-4 h-4 text-emerald-400" /></div>
            <div><h3 className="text-white font-semibold text-sm">AEO (Answer Engine Optimization)</h3><p className="text-gray-400 text-xs">Focuses on traditional answer surfaces — featured snippets, PAA, voice search. Overlaps with GEO as AI Overviews merge with snippets.</p></div>
          </div>
        </div>
        <p className="text-gray-400 text-sm mt-3">
          <Link to="/aeo-vs-geo-vs-seo" className="text-[#2ABFBF] hover:underline">Read the full comparison →</Link>
        </p>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-bold text-white mb-6">Frequently Asked Questions</h2>
        <Accordion type="single" collapsible className="space-y-3">
          {[
            { q: "Is LLMO a real discipline or just a buzzword?", a: "LLMO is a real, measurable discipline. Companies like Princeton and Georgia Tech have published peer-reviewed research on how structured data, entity signals, and content formatting influence LLM outputs. The results are reproducible and auditable." },
            { q: "Can you actually control what ChatGPT says?", a: "You can't control it, but you can significantly influence it through legitimate optimization. LLMs pull from web content, structured data, and authoritative sources. By ensuring your brand is well-represented across these sources with accurate, citation-worthy content, you shape how and when LLMs mention you." },
            { q: "Is LLMO only for large companies?", a: "No. LLMO is especially valuable for local and mid-size businesses because AI search often narrows results to specific recommendations. A well-optimized local business can outperform national competitors in AI search for local queries." },
          ].map((faq, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="bg-white/5 border border-white/10 rounded-xl px-5">
              <AccordionTrigger className="text-white text-left text-sm font-medium hover:no-underline">{faq.q}</AccordionTrigger>
              <AccordionContent className="text-gray-400 text-sm">{faq.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Cross-links */}
      <div className="grid sm:grid-cols-3 gap-4 mb-12">
        <Link to="/what-is-aeo" className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-4 hover:bg-emerald-500/10 transition-colors">
          <h3 className="text-white font-semibold text-sm mb-1">What Is AEO?</h3>
          <p className="text-gray-400 text-xs">Answer Engine Optimization</p>
        </Link>
        <Link to="/what-is-geo" className="bg-purple-500/5 border border-purple-500/20 rounded-xl p-4 hover:bg-purple-500/10 transition-colors">
          <h3 className="text-white font-semibold text-sm mb-1">What Is GEO?</h3>
          <p className="text-gray-400 text-xs">Generative Engine Optimization</p>
        </Link>
        <Link to="/aeo-vs-geo-vs-seo" className="bg-[#2ABFBF]/5 border border-[#2ABFBF]/20 rounded-xl p-4 hover:bg-[#2ABFBF]/10 transition-colors">
          <h3 className="text-white font-semibold text-sm mb-1">AEO vs GEO vs SEO</h3>
          <p className="text-gray-400 text-xs">Full comparison guide</p>
        </Link>
      </div>

      <div className="bg-[#111] border border-white/10 rounded-2xl p-8 text-center">
        <h2 className="text-2xl font-bold text-white mb-3">Make AI Recommend Your Brand</h2>
        <p className="text-gray-400 mb-6">See how LLMs currently perceive your business — and what to fix first.</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/services/llm-optimization"><Button className="bg-[#2ABFBF] hover:bg-[#1E9999] text-black font-semibold">LLM Optimization Services</Button></Link>
          <Link to="/free-ai-visibility-audit"><Button variant="outline" className="border-white/20 text-gray-300">Free AI Visibility Audit</Button></Link>
        </div>
      </div>
    </article>
  </div>
);

export default WhatIsLLMOPage;
